import React, { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Skeleton from "@mui/material/Skeleton";
import { toast } from "react-toastify";

import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CancelIcon from "@mui/icons-material/Cancel";

import getServices from "../../Services/get-service";

import ActiveInvestmentDetailsTab from "./ActiveInvestmentDetailsTab";
import ActiveInvestmentTransactionsTab from "./ActiveInvestmentTransactionsTab";
import ActiveInvestmentUpdatesTab from "./ActiveInvestmentUpdatesTab";
import ValuationRequestModal from "./ValuationRequestModal";
import ValuationSuccessModal from "./ValuationSuccessModal";

// Currency formatter
const currencyFormatter = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 2,
});

// Format currency
const formatCurrency = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
  ) {
    return "N/A";
  }

  return currencyFormatter.format(Number(value));
};

// Convert API date values to valid dates
const parseDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalizedValue =
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

// Format dates
const formatDate = (value) => {
  const date = parseDate(value);

  if (!date) return "N/A";

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Calculate approximate months remaining until liquidation
const calculateMonthsRemaining = (eligibilityDate) => {
  const date = parseDate(eligibilityDate);

  if (!date) return null;

  const millisecondsRemaining = date.getTime() - Date.now();

  const millisecondsPerMonth = 1000 * 60 * 60 * 24 * 30.44;

  return Math.max(0, Math.ceil(millisecondsRemaining / millisecondsPerMonth));
};

function ActiveInvestmentDrawer({ open, handleClose, property, onBuyClick }) {
  // Tabs
  const [activeTab, setActiveTab] = useState("details");

  // Investment details
  const [investmentData, setInvestmentData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  // Transactions
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState(false);

  // Updates
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [updatesError, setUpdatesError] = useState(false);

  // Valuation
  const [valuationPending, setValuationPending] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  // Selected property ID
  const propertyId = property?.propertyId ?? property?.id;

  // Reset drawer state when changing investments
  useEffect(() => {
    if (!open) return;

    setActiveTab("details");
    setValuationPending(false);
    setActiveModal(null);
  }, [open, propertyId]);

  // Fetch active investment details
  useEffect(() => {
    if (!open || propertyId == null) {
      setInvestmentData(null);
      setLoadingDetails(false);
      setDetailsError("");
      return;
    }

    let cancelled = false;

    const fetchInvestmentDetails = async () => {
      setLoadingDetails(true);
      setDetailsError("");
      setInvestmentData(null);

      try {
        const response = await getServices.getActiveInvestment(propertyId);
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Unable to fetch investment details");
        }

        if (!data) {
          throw new Error("Investment details unavailable");
        }

        if (!cancelled) {
          setInvestmentData(data);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Unable to fetch investment details";

          toast.error(message);
          setDetailsError(message);
        }
      } finally {
        if (!cancelled) {
          setLoadingDetails(false);
        }
      }
    };

    fetchInvestmentDetails();

    return () => {
      cancelled = true;
    };
  }, [open, propertyId]);

  // Use the property ID returned by the details endpoint
  const transactionPropertyId = investmentData?.propertyId ?? propertyId;

  // Fetch transactions
  useEffect(() => {
    if (
      !open ||
      activeTab !== "transactions" ||
      transactionPropertyId == null
    ) {
      setTransactions([]);
      setLoadingTransactions(false);
      setTransactionsError(false);
      return;
    }

    let cancelled = false;

    const fetchTransactions = async () => {
      setLoadingTransactions(true);
      setTransactionsError(false);
      setTransactions([]);

      try {
        const response = await getServices.getRentalsTransactionHistory(
          transactionPropertyId,
        );
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Unable to fetch transactions");
        }

        if (!Array.isArray(data?.content)) {
          throw new Error("Invalid transaction response");
        }

        if (!cancelled) {
          setTransactions(data.content);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Unable to fetch transactions";

          toast.error(message);
          setTransactionsError(true);
          setTransactions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTransactions(false);
        }
      }
    };

    fetchTransactions();

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, transactionPropertyId]);

  // Fetch property updates
  useEffect(() => {
    if (!open || activeTab !== "updates" || transactionPropertyId == null) {
      setUpdates([]);
      setLoadingUpdates(false);
      setUpdatesError(false);
      return;
    }

    let cancelled = false;

    const fetchUpdates = async () => {
      setLoadingUpdates(true);
      setUpdatesError(false);
      setUpdates([]);

      try {
        const response = await getServices.getPropertyPhases(
          transactionPropertyId,
        );
        const { statusCode, description, data } = response?.data ?? {};

        if (statusCode !== 200) {
          throw new Error(description || "Unable to fetch property updates");
        }

        if (!cancelled) {
          setUpdates(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.description ||
            error?.message ||
            "Unable to fetch property updates";

          toast.error(message);
          setUpdatesError(true);
          setUpdates([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingUpdates(false);
        }
      }
    };

    fetchUpdates();

    return () => {
      cancelled = true;
    };
  }, [open, activeTab, transactionPropertyId]);

  // Calculate liquidation eligibility using the actual eligibility date
  const monthsRemaining = calculateMonthsRemaining(
    investmentData?.liquidationEligibilityDate,
  );

  // Map API response
  const mockData = investmentData
    ? {
        title: investmentData.productName ?? "N/A",

        location: investmentData.location ?? "N/A",

        image: investmentData.propertyImages?.[0]?.imageUrl ?? null,

        type: investmentData.investmentType ?? "N/A",

        currentBalance: formatCurrency(investmentData.amount),

        units: String(investmentData.unitBought ?? "N/A"),

        totalReturn: formatCurrency(investmentData.profit ?? "N/A"),

        annualDividend: formatCurrency(investmentData.annualDividend),

        lastDividendPaid: formatDate(investmentData.lastDividendPaidDate),

        nextDividend: formatDate(investmentData.nextDividendDate),

        holdingSince: formatDate(investmentData.startDate),

        liquidationEligibility: formatDate(
          investmentData.liquidationEligibilityDate,
        ),

        monthsRemaining,

        originalInvestmentCost: investmentData.amount ?? 0,

        dateOfValuationRequest: formatDate(Date.now()),

        estimatedCompletion: formatDate(
          investmentData.maturityDate ?? investmentData.endDate,
        ),
      }
    : null;

  // Liquidation eligibility
  const isEligibleForLiquidation = monthsRemaining === 0 && !valuationPending;

  const liquidationButtonText = valuationPending
    ? "Liquidation requested"
    : monthsRemaining === null
      ? "Eligibility unavailable"
      : monthsRemaining > 0
        ? `Eligible in ${monthsRemaining} mo`
        : "Liquidate";

  // Submit valuation request
  const handleSubmitValuation = () => {
    setValuationPending(true);
    setActiveModal("success");
  };

  // Available tabs
  const tabs = [
    { id: "details", label: "Details" },
    { id: "transactions", label: "Transactions" },
    { id: "updates", label: "Updates" },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", md: 450 },
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Header */}
      <div className="relative h-[250px] w-full bg-gray-200 flex-shrink-0">
        {loadingDetails ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : mockData?.image ? (
          <img
            src={mockData.image}
            alt={mockData.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400 text-sm">
            No image available
          </div>
        )}

        {/* Image Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close investment details"
          className="absolute top-4 left-4 z-10 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
        >
          <CancelIcon fontSize="small" />
        </button>

        {/* Property Details */}
        <div className="absolute bottom-4 left-4 right-4 z-10">
          {loadingDetails ? (
            <div className="space-y-2">
              <Skeleton variant="rounded" width={70} height={22} />
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={20} />
            </div>
          ) : mockData ? (
            <>
              <div className="flex gap-2 mb-2 flex-wrap">
                <span className="bg-[#007BFF] text-white text-[12px] font-bold px-2.5 py-1 rounded-md">
                  {mockData.type}
                </span>

                {valuationPending && (
                  <span className="bg-[#FFF4E5] text-[#FF9800] text-[12px] font-bold px-2.5 py-1 rounded-md">
                    Valuation pending
                  </span>
                )}
              </div>

              <h1
                className="text-white text-[24px] font-bold truncate"
                title={mockData.title}
              >
                {mockData.title}
              </h1>

              <p className="text-white text-[14px] flex items-center opacity-90 truncate">
                <PlaceOutlinedIcon
                  sx={{ fontSize: 16 }}
                  className="mr-1 flex-shrink-0"
                />
                {mockData.location}
              </p>
            </>
          ) : null}
        </div>
      </div>

      {/* Drawer Content */}
      <div className="p-6 overflow-y-auto">
        {loadingDetails ? (
          <div className="space-y-4">
            <Skeleton variant="text" width={150} height={20} />
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="rounded" height={48} />
            <Skeleton variant="rounded" height={220} />
          </div>
        ) : detailsError || !mockData ? (
          <div role="alert" className="text-center text-sm text-gray-500 py-10">
            {detailsError || "Unable to load investment details."}
          </div>
        ) : (
          <>
            {/* Investment Summary */}
            <div className="flex justify-between items-end mb-4">
              <div>
                <p className="text-[#808285] text-[12px] mb-1">
                  Investment amount
                </p>

                <p className="text-back-color2 text-[32px] font-bold leading-none tracking-tight">
                  {mockData.currentBalance}
                </p>

                {mockData.totalReturn !== "N/A" && (
                  <p className="text-[#00BC67] text-[12px] font-medium mt-1">
                    {mockData.totalReturn}
                  </p>
                )}
              </div>

              <div className="text-right">
                <p className="text-[#808285] text-[12px] mb-1">Units</p>

                <p className="text-back-color2 text-[18px] font-bold">
                  {mockData.units}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 mt-6">
              <button
                type="button"
                onClick={() => onBuyClick?.(mockData)}
                className="w-full py-3 rounded-lg bg-[#00BC67] hover:bg-[#00A859] transition-colors text-white font-[600] text-[14px]"
              >
                Buy more units
              </button>

              <button
                type="button"
                disabled={!isEligibleForLiquidation}
                onClick={() => setActiveModal("request")}
                className={`w-full py-3 rounded-lg border font-[600] text-[13px] transition-colors ${
                  isEligibleForLiquidation
                    ? "border-[#00BC67] text-[#00BC67] hover:bg-[#00BC67] hover:text-white"
                    : "border-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {liquidationButtonText}
              </button>
            </div>

            {/* Liquidation Information */}
            <div className="flex items-start gap-2 p-3 bg-[#F4FBF7] rounded-lg mt-3 text-[12px] text-[#00A859]">
              <InfoOutlinedIcon
                sx={{ fontSize: 16 }}
                className="flex-shrink-0"
              />

              <span>
                {valuationPending
                  ? "Awaiting updated property valuation before Secondary Market listing."
                  : monthsRemaining === null
                    ? "Liquidation eligibility date is not available yet."
                    : monthsRemaining > 0
                      ? `You'll be notified when your investment becomes eligible for liquidation on ${mockData.liquidationEligibility}.`
                      : "Your investment is now eligible for liquidation. You can request a valuation."}
              </span>
            </div>

            {/* Tabs */}
            <div
              role="tablist"
              aria-label="Investment information"
              className="flex border-b border-gray-100 mt-6"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 text-center py-3 text-[14px] transition-colors ${
                    activeTab === tab.id
                      ? "text-back-color2 font-bold border-b-2 border-[#00BC67]"
                      : "text-[#808285] font-[500]"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div role="tabpanel" className="mt-6">
              {activeTab === "details" && (
                <ActiveInvestmentDetailsTab mockData={mockData} />
              )}

              {activeTab === "transactions" && (
                <ActiveInvestmentTransactionsTab
                  transactions={transactions}
                  loadingTransactions={loadingTransactions}
                  error={transactionsError}
                />
              )}

              {activeTab === "updates" && (
                <ActiveInvestmentUpdatesTab
                  updates={updates}
                  loadingUpdates={loadingUpdates}
                  error={updatesError}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Valuation Modals */}
      {mockData && (
        <>
          <ValuationRequestModal
            open={activeModal === "request"}
            handleClose={() => setActiveModal(null)}
            mockData={mockData}
            onSubmit={handleSubmitValuation}
          />

          <ValuationSuccessModal
            open={activeModal === "success"}
            handleClose={() => setActiveModal(null)}
          />
        </>
      )}
    </Drawer>
  );
}

export default ActiveInvestmentDrawer;
